import React, { useState, useMemo, useContext, useEffect } from "react";
import { Form, Button, Pagination, Row, Col, Badge } from "react-bootstrap";
import { PlayCircleFill, CloudArrowUp, Youtube, CheckCircleFill, HourglassSplit, ExclamationTriangleFill, Upload, ArrowUpCircle,CheckCircle,ExclamationTriangle } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../jsx/UserContext";
import { useDashboardStatus } from "../utils/DashboardstatusLogic";
import { FileuploadLogic } from "../utils/FileUploadLogic";
import NavbarComp1 from "../components/Navbar1";

const FileUpload = () => {
  const { userSession } = useContext(UserContext);
  const navigate = useNavigate();
  const { stats, recentVideos, dashboard_data } = useDashboardStatus(userSession);
  const [activeSection, setActiveSection] = useState("media");
  const [currentPage, setCurrentPage] = useState(1);
  const videosPerPage = 8;
  const [fileError, setFileError] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const upload = FileuploadLogic(userSession, activeSection);
  const isExpert = userSession?.user_type === "expert";
  const outputOptions = dashboard_data?.data?.form_data?.output_options || [];

  /* ============ MEDIA FORM STATES ============ */

  const [mediaNarrator, setMediaNarrator] = useState("m5");
  const [mediaDescription, setMediaDescription] = useState("");
  const [mediaLanguage, setMediaLanguage] = useState("");
  const [mediaDescriptionType, setMediaDescriptionType] = useState("");
  const [mediaVoice, setMediaVoice] = useState("Male");
  const [mediaFormats, setMediaFormats] = useState(["video"]);

  /* ============ YOUTUBE  FORM STATES ============ */

  const [youtubeTitle, setYoutubeTitle] = useState("");
  const [youtubeDescription, setYoutubeDescription] = useState("");
  const [youtubeNarrator, setYoutubeNarrator] = useState("m5");
  const [youtubeLanguage, setYoutubeLanguage] = useState("");
  const [youtubeDescriptionType, setYoutubeDescriptionType] = useState("");
  const [youtubeVoice, setYoutubeVoice] = useState("Male");
  const [youtubeFormats, setYoutubeFormats] = useState(["video"]);

 /* ============ SET DEFAULTS ONCE DASHBOARD DATA LOADS ============ */

  useEffect(() => {
    try {
      const formData = dashboard_data?.data?.form_data;
      if (!formData) return;

      const firstNarrator = formData.model_choices?.[0]?.[0]?.key;
      const firstLanguage = formData.languages?.[0]?.[0]?.[0];
      const firstDescType = formData.description_types?.[0]?.[0];

      if (firstNarrator) {
        setMediaNarrator(firstNarrator);
        setYoutubeNarrator(firstNarrator);
      }
      if (firstLanguage) {
        setMediaLanguage(firstLanguage);
        setYoutubeLanguage(firstLanguage);
      }
      if (firstDescType) {
        setMediaDescriptionType(firstDescType);
        setYoutubeDescriptionType(firstDescType);
      }
    } catch (err) {
      console.error("Failed to set form defaults:", err);
    }
  }, [dashboard_data]);

 /* ============ RECENT VIDEOS ============ */

  const totalPages = Math.max(1, Math.ceil(recentVideos.length / videosPerPage));
  const currentVideos = useMemo(() => {
    const start = (currentPage - 1) * videosPerPage;
    return recentVideos.slice(start, start + videosPerPage);
  }, [recentVideos, currentPage]);

  const goToVideo = (videoId) => {
    isExpert
      ? navigate(`/expert/${videoId}`)
      : navigate(`/videodescription/${videoId}`);
  };

 /* ============ FILEUPLOAD ============ */

  const handleGenerate = (e) => {
    e.preventDefault();
    let hasError = false;
    if (activeSection === "media" && !upload.file) {
      setFileError(true);
      hasError = true;
    } else {
      setFileError(false);
    }
    // Title is hidden and hardcoded — skip validation
    setTitleError(false);
    if (hasError) return;
    if (activeSection === "media") {
      upload.handleSubmit(e, {
        mediaTitle: upload.mediaTitle,
        mediaDescription,
        mediaNarrator,
        mediaLanguage,
        mediaDescriptionType,
        mediaVoice,
        mediaFormats
      });
    } else {
      upload.handleYoutubeSubmit(e, {
        youtubeUrl: upload.youtubeUrl,
        youtubeTitle,
        youtubeDescription,
        youtubeNarrator,
        youtubeLanguage,
        youtubeDescriptionType,
        youtubeVoice,
        youtubeFormats
      });
    }
  };

  const handleFormatChange = (type, value) => {
    if (type === "media") {
      setMediaFormats((prev) => {
        let updated;
        if (prev.includes(value)) {
          updated = prev.filter((item) => item !== value);
        } else {
          updated = [...prev, value];
        }
        return updated;
      });
    } else {
      setYoutubeFormats((prev) => {
        let updated;
        if (prev.includes(value)) {
          updated = prev.filter((item) => item !== value);
        } else {
          updated = [...prev, value];
        }
        return updated;
      });
    }
  };

  return (
    <div className="vh-100 d-flex flex-column bg-white overflow-hidden">
      <NavbarComp1 />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .vh-100 {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
          letter-spacing: -0.01em;
          }
        .form-panel::-webkit-scrollbar { display: none; } /* Hide scrollbar Chrome/Safari */

        .dashboard-container {
          flex: 1;
          display: flex;
          overflow: hidden;
          padding: 16px;
          gap: 16px;
          background-color: #f1f3f5;
        }

        .form-panel {
          width: 450px;
          background: white;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          border: 1px solid #e9ecef;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }

        .content-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 12px;
          overflow: hidden;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
        }

        .stat-box {
          padding: 12px 16px;
          border-radius: 8px;
          height: 75px;
          color: white;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .library-card {
          flex: 1;
          background: white;
          border-radius: 12px;
          padding: 15px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid #dee2e6;
        }

        .video-scroll-area {
           flex: 1;
          overflow-y: auto;
          scrollbar-width: thin;
          padding-right: 5px;
        }

        .compact-label {
          font-size: 15px;
          margin-bottom: 6px;
          display: block;
          color: #495057;
        }

        .upload-area {
          border: 1px dashed #bbb;
          background: #fffafa;
          padding: 20px;
          border-radius: 10px;
          cursor: pointer;
          text-align: center;
        }

        .custom-primary-btn {
          background-color: #B0004E !important;
          color: white !important;
          border: none !important;
        }

        .custom-primary-btn:hover {
          background-color: #960040 !important;
        }

        @media (max-width: 992px) {
          .dashboard-container {
            flex-direction: column;
            overflow-y: auto;
          }
          .vh-100 { height: auto !important; overflow: auto !important; }

                   .form-panel { width: 100%; height: auto; }


          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>

      <div className="dashboard-container">
 {/* ============ LEFT PANEL - FILEUPLOAD ============*/}
                   
        <div className="form-panel p-4">
          <h5 className="fw-bold mb-1" style={{ color: "#B0004E" }}>
            Accessible Video Description Generator
          </h5>

          <p className="text-muted small mb-3">
            Generate descriptive audio for video content to assist visually impaired users.
          </p>

          <div className="d-flex gap-2 mb-3 bg-light p-1 rounded">

            <Button
              size="sm"
              variant="transparent"
              className="flex-grow-1 border-0 fw-bold"
              style={{
                backgroundColor:
                  activeSection === "media" ? "#B0004E" : "transparent",
                color:
                  activeSection === "media" ? "#fff" : "#000"
              }}
              onClick={() => setActiveSection("media")}
            >
              <Upload size={14} /> Upload Video
            </Button>

            <Button
              size="sm"
              variant="transparent"
              className="flex-grow-1 border-0 fw-bold"
              style={{
                backgroundColor:
                  activeSection === "youtube" ? "#B0004E" : "transparent",
                color:
                  activeSection === "youtube" ? "#fff" : "#000"
              }}
              onClick={() => setActiveSection("youtube")}
            >
              <Youtube size={14} /> YouTube Link
            </Button>
          </div>

          <Form>
             {/* ============ MEDIA SECTION ============*/}

            {activeSection === "media" ? (
              <>
                <div
                  className="upload-area mb-3"
                  onClick={() =>
                    document.getElementById("fileInput").click()
                  }
                  onDragOver={(e) => {
                    e.preventDefault();
                    upload.setDragging(true);
                  }}
                  onDragLeave={() => {
                    upload.setDragging(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    upload.setDragging(false);

                    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                      upload.setFile(e.dataTransfer.files[0]);
                      setFileError(false);
                    }
                  }}
                  style={{
                    border: upload.dragging
                      ? "2px dashed #B0004E"
                      : "1px dashed #bbb",
                    background: upload.dragging
                      ? "#fff0f6"
                      : "#fffafa",
                    transition: "all 0.2s ease"
                  }}
                >

                  <CloudArrowUp size={30} color="#B0004E" />

                  <div
                    className="fw-bold small mt-1"
                    style={{ color: "#B0004E" }}
                  >
                    {upload.file
                      ? upload.file.name
                      : "Drag and Drop or Click to Upload Video"}
                  </div>

                  <Form.Control
                    id="fileInput"
                    type="file"
                    hidden
                    onChange={(e) => {
                      upload.handleFileChange(e);

                      if (e.target.files.length > 0) {
                        setFileError(false);
                      }
                    }}
                  />

                  {fileError && (
                    <div
                      className="text-danger fw-semibold mt-2"
                      style={{ fontSize: "13px" }}
                    >
                      Please upload a video.
                    </div>
                  )}
                </div>

                <Row className="g-2 mb-2">
                  <Col xs={6} style={{ display: "none" }}>
                    <Form.Label className="compact-label">
                      Video Title
                    </Form.Label>

                    <Form.Control
                      size="sm"
                      placeholder="Enter Video Title"
                      value={upload.mediaTitle}
                      onChange={(e) => {
                        upload.setMediaTitle(e.target.value);
                        if (e.target.value.trim()) setTitleError(false);
                      }}
                    />
                  </Col>

                  <Col xs={6} style={{ display: "none" }}>
                    <Form.Label className="compact-label">
                      Video Description
                    </Form.Label>

                    <Form.Control
                      as="textarea"
                      rows={3}
                      size="sm"
                      placeholder="Enter Description"
                      value={mediaDescription}
                      onChange={(e) =>
                        setMediaDescription(e.target.value)
                      }
                      style={{ resize: "none" }}
                    />
                  </Col>
                </Row>

                <Row className="g-2 mb-2">
                  <Col xs={6}>
                    <Form.Label className="compact-label">
                      Narrator
                    </Form.Label>

                    <Form.Select
                      size="sm"
                      value={mediaNarrator}
                      onChange={(e) =>
                        setMediaNarrator(e.target.value)
                      }
                    >
                      {dashboard_data?.data?.form_data?.model_choices?.[0]?.map(
                        (item, idx) => (
                          <option key={idx} value={item.key}>
                            {item.label}
                          </option>
                        )
                      )}
                    </Form.Select>
                  </Col>

                  <Col xs={6}>
                    <Form.Label className="compact-label">
                      Language
                    </Form.Label>

                    <Form.Select
                      size="sm"
                      value={mediaLanguage}
                      onChange={(e) =>
                        setMediaLanguage(e.target.value)
                      }
                    >
                      {dashboard_data?.data?.form_data?.languages?.[0]?.map(
                        (l, i) => (
                          <option key={i} value={l[0]}>
                            {l[1]}
                          </option>
                        )
                      )}
                    </Form.Select>
                  </Col>
                </Row>

                <Row className="g-2 mb-3">
                  <Col xs={6}>
                    <Form.Label className="compact-label">
                      Description Type
                    </Form.Label>

                    <Form.Select
                      size="sm"
                      value={mediaDescriptionType}
                      onChange={(e) =>
                        setMediaDescriptionType(e.target.value)
                      }
                    >
                      {dashboard_data?.data?.form_data?.description_types?.[0]?.map(
                        (item, idx) => (
                          <option key={idx} value={item}>
                            {item}
                          </option>
                        )
                      )}
                    </Form.Select>
                  </Col>

                  <Col xs={6}>
                    <Form.Label className="compact-label">
                      Voice
                    </Form.Label>

                    <Form.Select
                      size="sm"
                      value={mediaVoice}
                      onChange={(e) => setMediaVoice(e.target.value)}
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </Form.Select>

                  </Col>
                  {/* Generate As */}
                  <div className="mb-3">
                    <Form.Label className="compact-label">
                      Generate As
                    </Form.Label>

                    <div className="d-flex flex-wrap gap-4">
                      {outputOptions
                        .filter((option) => option.value !== "video")
                        // .filter((option) => option.value)
                        .map((option, index) => (
                          <Form.Check
                            key={index}
                            type="checkbox"
                            id={`${activeSection}-${option.value}`}
                            label={option.label}
                            className="compact-label"
                            checked={
                              activeSection === "media"
                                ? mediaFormats.includes(option.value)
                                : youtubeFormats.includes(option.value)
                            }
                            onChange={() =>
                              handleFormatChange(activeSection, option.value)
                            }
                          />
                        ))}
                    </div>
                  </div>

                </Row>

              </>
            ) : (
              <>
               {/* ============ YOUTUBE SECTION ============*/}

                <Form.Group className="mb-3">
                  <Form.Label className="compact-label">
                    YouTube URL
                  </Form.Label>

                  <Form.Control
                    size="sm"
                    placeholder="Paste YouTube URL"
                    value={upload.youtubeUrl}
                    onChange={(e) =>
                      upload.setYoutubeUrl(e.target.value)
                    }
                  />
                </Form.Group>

                <Row className="g-2 mb-2">
                  <Col xs={6} style={{ display: "none" }}>
                    <Form.Label className="compact-label">
                      YouTube Title
                    </Form.Label>

                    <Form.Control
                      size="sm"
                      placeholder="Enter YouTube Title"
                      value={youtubeTitle}
                      onChange={(e) => {
                        setYoutubeTitle(e.target.value);
                        if (e.target.value.trim()) setTitleError(false);
                      }}
                    />
                  </Col>

                  <Col xs={6} style={{ display: "none" }}>
                    <Form.Label className="compact-label">
                      YouTube Description
                    </Form.Label>

                    <Form.Control
                      as="textarea"
                      rows={3}
                      size="sm"
                      placeholder="Enter Description"
                      value={youtubeDescription}
                      onChange={(e) =>
                        setYoutubeDescription(e.target.value)
                      }
                      style={{ resize: "none" }}
                    />
                  </Col>
                </Row>

                <Row className="g-2 mb-2">
                  <Col xs={6}>
                    <Form.Label className="compact-label">
                      Narrator
                    </Form.Label>

                    <Form.Select
                      size="sm"
                      value={youtubeNarrator}
                      onChange={(e) =>
                        setYoutubeNarrator(e.target.value)
                      }
                    >
                      {dashboard_data?.data?.form_data?.model_choices?.[0]?.map(
                        (item, idx) => (
                          <option key={idx} value={item.key}>
                            {item.label}
                          </option>
                        )
                      )}
                    </Form.Select>
                  </Col>

                  <Col xs={6}>
                    <Form.Label className="compact-label">
                      Language
                    </Form.Label>

                    <Form.Select
                      size="sm"
                      value={youtubeLanguage}
                      onChange={(e) =>
                        setYoutubeLanguage(e.target.value)
                      }
                    >
                      {dashboard_data?.data?.form_data?.languages?.[0]?.map(
                        (l, i) => (
                          <option key={i} value={l[0]}>
                            {l[1]}
                          </option>
                        )
                      )}
                    </Form.Select>
                  </Col>
                </Row>

                <Row className="g-2 mb-3">
                  <Col xs={6}>
                    <Form.Label className="compact-label">
                      Description Type
                    </Form.Label>

                    <Form.Select
                      size="sm"
                      value={youtubeDescriptionType}
                      onChange={(e) =>
                        setYoutubeDescriptionType(e.target.value)
                      }
                    >
                      {dashboard_data?.data?.form_data?.description_types?.[0]?.map(
                        (item, idx) => (
                          <option key={idx} value={item}>
                            {item}
                          </option>
                        )
                      )}
                    </Form.Select>
                  </Col>

                  <Col xs={6}>
                    <Form.Label className="compact-label">
                      Voice
                    </Form.Label>

                    <Form.Select
                      size="sm"
                      value={youtubeVoice}
                      onChange={(e) => setYoutubeVoice(e.target.value)}
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </Form.Select>
                  </Col>
                  {/* Generate As */}
                  <div className="mb-3">
                    <Form.Label className="compact-label">
                      Generate As
                    </Form.Label>

                    <div className="d-flex flex-wrap gap-4">
                      {outputOptions
                        .filter((option) => option.value !== "video")
                        //  .filter((option) => option.value)
                        .map((option, index) => (
                          <Form.Check
                            key={index}
                            type="checkbox"
                            id={`${activeSection}-${option.value}`}
                            label={option.label}
                            className="compact-label"
                            checked={
                              activeSection === "media"
                                ? mediaFormats.includes(option.value)
                                : youtubeFormats.includes(option.value)
                            }
                            onChange={() =>
                              handleFormatChange(activeSection, option.value)
                            }
                          />
                        ))}
                    </div>
                  </div>
                </Row>
              </>
            )}

            <Button
              className="w-100 fw-bold py-2 custom-primary-btn"
              onClick={handleGenerate}
            >
              Start Generation
            </Button>
          </Form>
        </div>

             {/* ============ RIGHT PANEL - DASHBOARD STATUS CARD ============*/}

        <div className="content-panel">
          <div className="stats-grid">
            {/* {[
              {
                label: "Uploaded",
                val: stats.uploaded,
                color: "rgb(104,111,175)"
              },
              {
                label: "Processing",
                val: stats.processing,
                color: "rgb(193,64,6)"
              },
              {
                label: "Published",
                val: stats.published,
                color: "rgb(1,153,136)"
              },
              {
                label: "Corrupted",
                val: stats.corrupted,
                color: "rgb(201,134,32)"
              }
            ].map((s, i) => (
              <div
                key={i}
                className="stat-box"
                style={{ backgroundColor: s.color }}
              >
                <div className="fw-bold h5 mb-0">{s.val}</div>

                <div style={{ fontSize: "14px" }}>{s.label}</div>
              </div>
            ))} */}
            {[
  {
    label: "Uploaded",
    val: stats.uploaded,
    color: "rgb(104,111,175)",
    icon: <ArrowUpCircle size={28} />
  },
  {
    label: "Processing",
    val: stats.processing,
    color: "rgb(193,64,6)",
    icon: <HourglassSplit size={24} />
  },
  {
    label: "Published",
    val: stats.published,
    color: "rgb(1,153,136)",
    icon: <CheckCircle size={26} />
  },
  {
    label: "Corrupted",
    val: stats.corrupted,
    color: "rgb(201,134,32)",
    icon: <ExclamationTriangle size={26} />
  }
].map((s, i) => (
  <div
    key={i}
    className="stat-box"
    style={{
      backgroundColor: s.color,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between"
    }}
  >
    <div>
      <div className="fw-bold h4 mb-1">{s.val}</div>

      <div style={{ fontSize: "14px" }}>
        {s.label}
      </div>
    </div>

    <div
      style={{
        width: "42px",
        height: "42px",
        borderRadius: "50%",
        background: "rgba(255,255,255,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff"
      }}
    >
      {s.icon}
    </div>
  </div>
))}
          </div>

             {/* ============ RIGHT PANEL - RECENT VIDEOS ============*/}

          <div className="library-card">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-bold m-0">Recent Videos</h6>

              <Badge bg="light" text="dark" className="border">
                Total: {recentVideos.length}
              </Badge>
            </div>

            <div className="video-scroll-area">
              <Row className="g-2">
                {currentVideos.map((video) => (
                  <Col sm={6} md={4} xl={3} key={video.id}>
                    <div
                      className="border rounded p-2 h-100 bg-white"
                      onClick={() => goToVideo(video.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <div
                        className="position-relative rounded overflow-hidden mb-1"
                        style={{
                          aspectRatio: "16/9",
                          background: "#000"
                        }}
                      >
                        <img
                          src={video.thumbnail}
                          className="w-100 h-100 object-fit-cover opacity-75"
                          alt=""
                        />

                        <PlayCircleFill
                          size={24}
                          className="position-absolute top-50 start-50 translate-middle text-white"
                        />
                      </div>

                      <div
                        className="fw-bold text-truncate"
                        style={{ fontSize: "13px" }}
                      >
                        {video.title}
                      </div>
                    
                      <div className="d-flex justify-content-between mt-1" 
                      style={{ fontSize: "12px" }}>
                    <span className="fw-bold"
                      style={{
                          color:
                          video.status === "Failed"? "red"
                          : ["Queued", "Merging", "In progress"].includes(video.status)? "#8000FF"
                          : "green"
                        }}
                    aria-label={`Status: ${
                    video.status === "Merging"? "In progress": video.status || "Completed"}`}>
                    {video.status === "Merging"? "In progress": video.status || "Completed"}
                </span>
              </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            <div className="pt-2 d-flex justify-content-center">
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                />

                <Pagination.Item active>
                  {currentPage}
                </Pagination.Item>

                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                />
              </Pagination>
            </div>
          </div>
        </div>
      </div>

      <footer
        className="bg-white border-top p-2 text-center"
        style={{ fontSize: "11px", color: "#666" }}
      >
        © 2026 <strong>Amrita CREATE</strong> | AI Video Accessibility Platform
      </footer>
    </div>
  );
};

export default FileUpload;